<!-- src/lib/components/NavSidebar.svelte -->
<!--
  Contextual content sidebar. Swaps its body based on which section is
  active in the NavRail to its left. Collapsible via ⌘\ (handled by the
  parent — this component just animates its width).

  Post-Round-0 reorganization (2026-04-11). Seven sections:

    dev      → single view (the IDE / terminal / files shell)
    tasks    → workspace + project list + view switcher (Board / Table /
               Gallery / Canvas) + Planning (Goals / Milestones /
               Portfolio / Workload) + My Tasks
    agents   → Running / Activity / Rules / Personas
    studio   → Studio canvas + Brand Assets palette
    drive    → Google Drive file browser
    chat     → Channels list
    settings → Appearance / Integrations / Data / Shortcuts (Round 5
               will move this to a full-page subapp)
-->
<script lang="ts">
  import { pmStore, hydrateBoards, hydrateViewConfigs, openWorkspaceSession, saveActiveWorkspaceId, hydrateProjects, hydrateRepos } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';
  import type { NavSection } from './NavRail.svelte';

  export type Tab =
    | 'overview' | 'board' | 'table' | 'gallery' | 'canvas'
    | 'agents' | 'activity' | 'tasks' | 'goals' | 'milestones'
    | 'portfolio' | 'workload' | 'pulse' | 'rules' | 'personas'
    | 'chat' | 'brand' | 'drive' | 'studio' | 'dev';

  interface Props {
    section:           NavSection;
    activeTab:         Tab;
    onSelectTab:       (tab: Tab) => void;
    activeViewConfigId?: string | null;
    onSelectViewConfig?: (id: string) => void;
    onOpenSettings?:   (category?: string) => void;
  }
  let { section, activeTab, onSelectTab, activeViewConfigId = null, onSelectViewConfig, onOpenSettings }: Props = $props();

  // Per-section nav items. Order matters.
  interface NavItem { id: Tab; label: string; }

  const projectViews: NavItem[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'board',    label: 'Board'    },
    { id: 'table',    label: 'Table'    },
    { id: 'gallery',  label: 'Gallery'  },
    { id: 'canvas',   label: 'Canvas'   },
  ];

  const planningViews: NavItem[] = [
    { id: 'goals',      label: 'Goals'      },
    { id: 'milestones', label: 'Milestones' },
    { id: 'portfolio',  label: 'Portfolio'  },
    { id: 'workload',   label: 'Workload'   },
    { id: 'pulse',      label: 'Pulse'      },
  ];

  const agentViews: NavItem[] = [
    { id: 'agents',   label: 'Running'  },
    { id: 'activity', label: 'Activity' },
    { id: 'rules',    label: 'Rules'    },
    { id: 'personas', label: 'Personas' },
  ];

  // Studio side palettes: Canvas (the actual design surface) and Brand
  // Assets (the image source palette). Drive moved to its own top-level.
  const studioViews: NavItem[] = [
    { id: 'studio', label: 'Canvas'       },
    { id: 'brand',  label: 'Brand Assets' },
  ];

  // Workspace switcher dropdown state
  let wsDropdownOpen = $state(false);

  async function switchWorkspace(wsId: string) {
    if (pmStore.activeWorkspaceId === wsId) { wsDropdownOpen = false; return; }
    openWorkspaceSession(wsId);
    pmStore.activeWorkspaceId = wsId;
    saveActiveWorkspaceId(wsId);
    pmStore.activeProjectId   = null;
    pmStore.activeBoardId     = null;
    wsDropdownOpen = false;
    await hydrateProjects(wsId);
    await hydrateRepos(wsId);
  }

  async function switchProject(projectId: string) {
    await hydrateBoards(projectId);
  }

  // Repo filter state — null means "All repos"
  let selectedRepoId = $state<string | null>(null);

  // ── Custom Views (view_configs) ──────────────────────────────────────
  let showNewViewForm  = $state(false);
  let newViewName      = $state('');
  let newViewType      = $state<'board' | 'table'>('board');
  let newViewAllRepos  = $state(true);
  let creatingView     = $state(false);

  async function createViewConfig() {
    if (!newViewName.trim() || !pmStore.activeWorkspaceId) return;
    creatingView = true;
    try {
      await api('/api/v1/view-configs', {
        method: 'POST',
        body: JSON.stringify({
          workspace_id: pmStore.activeWorkspaceId,
          name: newViewName.trim(),
          view_type: newViewType,
          repo_ids: newViewAllRepos ? JSON.stringify(['*']) : JSON.stringify([]),
        }),
      });
      // Refresh the list
      await hydrateViewConfigs(pmStore.activeWorkspaceId);
      newViewName     = '';
      newViewType     = 'board';
      newViewAllRepos = true;
      showNewViewForm = false;
    } catch (e) {
      console.warn('Failed to create view config:', e);
    } finally {
      creatingView = false;
    }
  }

  async function deleteViewConfig(id: string) {
    if (!pmStore.activeWorkspaceId) return;
    try {
      await api(`/api/v1/view-configs/${id}`, { method: 'DELETE' });
      await hydrateViewConfigs(pmStore.activeWorkspaceId);
    } catch (e) {
      console.warn('Failed to delete view config:', e);
    }
  }

  // Projects filtered by the selected repo
  const filteredProjects = $derived(
    selectedRepoId === null
      ? pmStore.projects
      : pmStore.projects.filter(p => p.repo_id === selectedRepoId)
  );

  const sectionTitles: Record<NavSection, string> = {
    dev:      'Development',
    tasks:    'Tasks',
    agents:   'Agents',
    studio:   'Studio',
    drive:    'Drive',
    chat:     'Chat',
    settings: 'Settings',
  };
</script>

<aside class="nav-sidebar" role="navigation" aria-label={sectionTitles[section]}>
  <header class="sidebar-header">
    <h2 class="section-title">{sectionTitles[section]}</h2>
  </header>

  <div class="sidebar-body">
    {#if section === 'tasks'}
      <!-- Unified tasks section: workspace + project list + views +
           planning + my-tasks. Replaces the old project / planning /
           tasks trio. Kanban board is the default view. -->
      <div class="workspace-switcher">
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div class="workspace-name" onclick={() => { wsDropdownOpen = !wsDropdownOpen; }}>
          <span>{pmStore.workspaces.find(w => w.id === pmStore.activeWorkspaceId)?.name ?? 'AgentBoard'}</span>
          <span class="chevron" class:open={wsDropdownOpen}>⌄</span>
        </div>
        {#if wsDropdownOpen}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div class="ws-dropdown-backdrop" onclick={() => { wsDropdownOpen = false; }}></div>
          <ul class="ws-dropdown" role="menu">
            {#each pmStore.workspaces as ws (ws.id)}
              <li>
                <button
                  role="menuitem"
                  class="ws-dropdown-item"
                  class:active={ws.id === pmStore.activeWorkspaceId}
                  onclick={() => switchWorkspace(ws.id)}
                >
                  <span class="ws-dot" class:ws-dot-active={ws.id === pmStore.activeWorkspaceId}></span>
                  <span>{ws.name}</span>
                  {#if ws.id === pmStore.activeWorkspaceId}
                    <span class="ws-check" aria-hidden="true">✓</span>
                  {/if}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>

      {#if pmStore.repos.length > 0}
        <div class="subsection">
          <span class="subsection-label">Repo</span>
          <div class="repo-selector">
            <select
              class="repo-select"
              value={selectedRepoId ?? ''}
              onchange={(e) => {
                const v = (e.currentTarget as HTMLSelectElement).value;
                selectedRepoId = v === '' ? null : v;
              }}
            >
              <option value="">All repos</option>
              {#each pmStore.repos as repo (repo.id)}
                <option value={repo.id}>{repo.name}</option>
              {/each}
            </select>
          </div>
        </div>
      {/if}

      {#if filteredProjects.length > 0}
        <div class="subsection">
          <span class="subsection-label">Projects</span>
          <ul class="subnav">
            {#each filteredProjects as p (p.id)}
              <li>
                <button
                  class="subnav-btn"
                  class:active={pmStore.activeProjectId === p.id}
                  onclick={() => switchProject(p.id)}
                >
                  <span class="dot" style="background: {pmStore.activeProjectId === p.id ? 'oklch(0.80 0.26 310)' : 'oklch(0.45 0.04 310 / 0.5)'}"></span>
                  <span>{p.name}</span>
                </button>
              </li>
            {/each}
          </ul>
        </div>
      {/if}

      <div class="subsection">
        <span class="subsection-label">Views</span>
        <ul class="subnav">
          {#each projectViews as v (v.id)}
            <li>
              <button
                class="subnav-btn"
                class:active={activeTab === v.id}
                onclick={() => onSelectTab(v.id)}
              >{v.label}</button>
            </li>
          {/each}
          <li>
            <button
              class="subnav-btn"
              class:active={activeTab === 'tasks'}
              onclick={() => onSelectTab('tasks')}
            >My Tasks</button>
          </li>
        </ul>
      </div>

      <div class="subsection">
        <span class="subsection-label">Planning</span>
        <ul class="subnav">
          {#each planningViews as v (v.id)}
            <li>
              <button
                class="subnav-btn"
                class:active={activeTab === v.id}
                onclick={() => onSelectTab(v.id)}
              >{v.label}</button>
            </li>
          {/each}
        </ul>
      </div>

      <div class="subsection">
        <div class="subsection-header">
          <span class="subsection-label">Custom Views</span>
          <button
            class="new-view-btn"
            onclick={() => { showNewViewForm = !showNewViewForm; }}
            title="New view"
          >+</button>
        </div>

        {#if showNewViewForm}
          <form
            class="new-view-form"
            onsubmit={(e) => { e.preventDefault(); createViewConfig(); }}
          >
            <input
              class="new-view-input"
              bind:value={newViewName}
              placeholder="View name…"
              disabled={creatingView}
            />
            <select class="new-view-select" bind:value={newViewType} disabled={creatingView}>
              <option value="board">Board</option>
              <option value="table">Table</option>
            </select>
            <label class="new-view-allrepos">
              <input type="checkbox" bind:checked={newViewAllRepos} disabled={creatingView} />
              <span>All repos</span>
            </label>
            <div class="new-view-btns">
              <button type="submit" class="new-view-submit" disabled={creatingView || !newViewName.trim()}>
                {creatingView ? '…' : 'Create'}
              </button>
              <button type="button" class="new-view-cancel" onclick={() => { showNewViewForm = false; newViewName = ''; }} disabled={creatingView}>
                Cancel
              </button>
            </div>
          </form>
        {/if}

        {#if pmStore.viewConfigs.length > 0}
          <ul class="subnav">
            {#each pmStore.viewConfigs as vc (vc.id)}
              <li class="view-config-item">
                <button
                  class="subnav-btn view-config-btn"
                  class:active={activeViewConfigId === vc.id}
                  onclick={() => onSelectViewConfig?.(vc.id)}
                >
                  <span class="vc-type-dot" class:is-table={vc.view_type === 'table'}></span>
                  <span class="vc-name">{vc.name}</span>
                  <span class="vc-type">{vc.view_type}</span>
                </button>
                <button
                  class="vc-delete-btn"
                  onclick={(e) => { e.stopPropagation(); deleteViewConfig(vc.id); }}
                  title="Delete view"
                  aria-label="Delete {vc.name}"
                >×</button>
              </li>
            {/each}
          </ul>
        {:else if !showNewViewForm}
          <p class="subnav-hint">No custom views yet. Click + to create one.</p>
        {/if}
      </div>

      {#if onOpenSettings}
        <button class="section-settings-link" onclick={() => onOpenSettings?.('labels')}>
          ⚙ Project settings
        </button>
      {/if}

    {:else if section === 'agents'}
      <ul class="subnav">
        {#each agentViews as v (v.id)}
          <li>
            <button
              class="subnav-btn"
              class:active={activeTab === v.id}
              onclick={() => onSelectTab(v.id)}
            >{v.label}</button>
          </li>
        {/each}
      </ul>
      <p class="subnav-hint">Running agents, activity feed, rules and personas.</p>
      {#if onOpenSettings}
        <button class="section-settings-link" onclick={() => onOpenSettings?.('agents')}>
          ⚙ Agent settings
        </button>
      {/if}

    {:else if section === 'studio'}
      <div class="workspace-switcher">
        <div class="workspace-name">
          <span>{pmStore.workspaces.find(w => w.id === pmStore.activeWorkspaceId)?.name ?? 'AgentBoard'}</span>
        </div>
        <div class="studio-project-hint">
          {pmStore.projects.find(p => p.id === pmStore.activeProjectId)?.name ?? 'No project selected'}
        </div>
      </div>
      <ul class="subnav">
        {#each studioViews as v (v.id)}
          <li>
            <button
              class="subnav-btn"
              class:active={activeTab === v.id}
              onclick={() => onSelectTab(v.id)}
            >{v.label}</button>
          </li>
        {/each}
      </ul>
      <p class="subnav-hint">Native design surface. Frames, layers, components, and auto-layout coming.</p>

    {:else if section === 'drive'}
      <ul class="subnav">
        <li>
          <button
            class="subnav-btn"
            class:active={activeTab === 'drive'}
            onclick={() => onSelectTab('drive')}
          >Browser</button>
        </li>
      </ul>
      <p class="subnav-hint">Connect Google Drive to browse and attach files from inside AgentBoard.</p>

    {:else if section === 'chat'}
      <ul class="subnav">
        <li>
          <button
            class="subnav-btn"
            class:active={activeTab === 'chat'}
            onclick={() => onSelectTab('chat')}
          >Channels</button>
        </li>
      </ul>
      <p class="subnav-hint">Intra-org chat — channels, threads, mentions.</p>

    {:else if section === 'dev'}
      <ul class="subnav">
        <li>
          <button
            class="subnav-btn"
            class:active={activeTab === 'dev'}
            onclick={() => onSelectTab('dev')}
          >Workspace</button>
        </li>
      </ul>
      <p class="subnav-hint">VS Code-style IDE: file tree, preview, terminal, git, GitHub.</p>
    {/if}
    <!-- Note: section === 'settings' is no longer handled here —
         App.svelte renders SettingsSubapp full-page in that case. -->
  </div>
</aside>

<style>
  .nav-sidebar {
    width: 240px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    /* Clearly elevated above the rail so the user can see the three
       distinct shell zones: rail (dark) → sidebar (elevated) → main view. */
    background: oklch(0.115 0.02 275);
    border-right: 1px solid oklch(0.35 0.06 275 / 0.35);
    overflow: hidden;
    position: relative;
  }
  .nav-sidebar::after {
    content: '';
    position: absolute;
    top: 0; bottom: 0; right: 0;
    width: 1px;
    background: linear-gradient(180deg,
      transparent 0%,
      oklch(0.72 0.28 310 / 0.22) 50%,
      transparent 100%);
    pointer-events: none;
  }

  .sidebar-header {
    flex-shrink: 0;
    padding: 0.9rem 1rem 0.6rem 1rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.08);
  }
  .section-title {
    margin: 0;
    font-size: 0.82rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.10em;
    color: oklch(0.85 0.10 310 / 0.9);
  }

  .sidebar-body {
    flex: 1;
    overflow-y: auto;
    padding: 0.75rem 0;
  }
  .sidebar-body::-webkit-scrollbar { width: 6px; }
  .sidebar-body::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.4);
    border-radius: 3px;
  }

  .subsection {
    padding: 0 0.5rem 0.6rem 0.5rem;
  }
  .subsection-label {
    display: block;
    padding: 0.35rem 0.5rem 0.15rem 0.5rem;
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.10em;
    color: oklch(0.55 0.04 310 / 0.55);
  }

  /* ── Workspace switcher ───────────────────────────────────────── */
  .workspace-switcher {
    padding: 0 0.5rem 0.5rem 0.5rem;
  }
  .workspace-name {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.7rem;
    background: oklch(0.14 0.02 275 / 0.60);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 6px;
    color: oklch(0.90 0.05 310);
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 100ms ease, border-color 100ms ease;
  }
  .workspace-name:hover {
    background: oklch(0.18 0.03 275);
    border-color: oklch(0.72 0.28 310 / 0.3);
  }
  .studio-project-hint {
    padding: 0.45rem 0.2rem 0 0.2rem;
    font-size: 0.72rem;
    color: oklch(0.58 0.04 310 / 0.72);
    line-height: 1.35;
  }
  .chevron {
    color: oklch(0.55 0.04 310 / 0.6);
    transition: transform 140ms ease;
  }
  .chevron.open {
    transform: rotate(180deg);
  }

  /* ── Workspace dropdown ──────────────────────────────────────── */
  .workspace-switcher {
    position: relative;
  }
  .ws-dropdown-backdrop {
    position: fixed;
    inset: 0;
    z-index: 49;
  }
  .ws-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 50;
    list-style: none;
    margin: 0;
    padding: 4px 0;
    background: oklch(0.13 0.02 275);
    border: 1px solid oklch(0.28 0.05 275 / 0.4);
    border-radius: 8px;
    box-shadow: 0 8px 24px oklch(0 0 0 / 0.5);
    min-width: 160px;
  }
  .ws-dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 12px;
    background: transparent;
    border: none;
    color: oklch(0.78 0.04 310);
    font-size: 0.82rem;
    text-align: left;
    cursor: pointer;
    transition: background 80ms ease, color 80ms ease;
  }
  .ws-dropdown-item:hover,
  .ws-dropdown-item.active {
    background: oklch(0.18 0.03 275);
    color: oklch(0.96 0.05 310);
  }
  .ws-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: oklch(0.45 0.04 310 / 0.5);
    flex-shrink: 0;
  }
  .ws-dot-active {
    background: oklch(0.72 0.28 310);
  }
  .ws-check {
    margin-left: auto;
    color: oklch(0.72 0.28 310);
    font-size: 0.75rem;
  }

  /* ── Repo selector ───────────────────────────────────────────── */
  .repo-selector {
    padding: 0 0.5rem 0 0.5rem;
  }
  .repo-select {
    width: 100%;
    background: oklch(0.14 0.02 275 / 0.60);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 6px;
    color: oklch(0.90 0.05 310);
    font-size: 0.82rem;
    padding: 0.4rem 0.6rem;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23888'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.6rem center;
    padding-right: 1.8rem;
    transition: border-color 100ms ease;
  }
  .repo-select:hover {
    border-color: oklch(0.72 0.28 310 / 0.3);
  }
  .repo-select option {
    background: oklch(0.12 0.02 275);
    color: oklch(0.90 0.05 310);
  }

  /* ── Nav lists ────────────────────────────────────────────────── */
  .subnav {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .subnav li {
    padding: 0 0.5rem;
  }
  .subnav-btn {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    background: transparent;
    border: none;
    border-radius: 6px;
    padding: 0.5rem 0.7rem;
    font-size: 0.88rem;
    text-align: left;
    color: oklch(0.78 0.04 310 / 0.85);
    cursor: pointer;
    transition: background-color 100ms ease, color 100ms ease;
  }
  .subnav-btn:hover {
    background: oklch(0.72 0.28 310 / 0.08);
    color: oklch(0.92 0.06 310);
  }
  .subnav-btn.active {
    background: oklch(0.72 0.28 310 / 0.16);
    color: oklch(0.96 0.12 310);
  }
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: background-color 100ms ease;
  }

  .subnav-hint {
    padding: 0.5rem 1rem;
    font-size: 0.68rem;
    color: oklch(0.45 0.04 310 / 0.5);
    margin: 0;
    line-height: 1.5;
  }
  .section-settings-link {
    display: block;
    width: calc(100% - 1rem);
    margin: 0.25rem 0.5rem 0.5rem 0.5rem;
    background: transparent;
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 5px;
    color: oklch(0.50 0.08 265 / 0.70);
    font-size: 0.70rem;
    text-align: left;
    padding: 0.35rem 0.7rem;
    cursor: pointer;
    transition: color 100ms ease, border-color 100ms ease, background 100ms ease;
    font-family: inherit;
  }
  .section-settings-link:hover {
    color: oklch(0.75 0.18 265 / 0.95);
    border-color: oklch(0.72 0.28 310 / 0.25);
    background: oklch(0.12 0.02 275 / 0.50);
  }

  /* Settings CSS moved to SettingsSubapp.svelte in Round 5. */
</style>
